import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, ChevronDown, ChevronRight, Clock, CheckCircle,
  XCircle, FileText, Upload, MessageSquare, Send, Lock,
  RefreshCw, AlertCircle, Calendar, Trash2, X, Paperclip, Download, Edit2, Image
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import api from '../services/api';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { resolveFileUrl } from '@/lib/utils';
import { GradeHeroCard, ScoreCard, FullReport, IntelAnalysisBox } from '../components/LOIVerificationComponents';

// Default brand colors (fallback)
const DEFAULT_PRIMARY = '#2B4B6A';
const DEFAULT_SECONDARY = '#F58A5A';

const isImageFile = (filename) => /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(filename);

// Animated gold border for upload box
const uploadGlowStyle = document.getElementById('loi-upload-glow-style') || (() => {
  const s = document.createElement('style');
  s.id = 'loi-upload-glow-style';
  s.textContent = `
    @keyframes loi-upload-glow {
      0%, 100% { border-color: #E5A225; box-shadow: 0 0 8px rgba(229,162,37,0.2); }
      50% { border-color: #F59E0B; box-shadow: 0 0 16px rgba(245,158,11,0.4); }
    }
  `;
  document.head.appendChild(s);
  return s;
})();

const QuoteRequestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userTheme } = useTheme();
  const { user } = useAuth();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSteps, setExpandedSteps] = useState({});
  const [stepAnswers, setStepAnswers] = useState({});
  const [submitting, setSubmitting] = useState({});
  const [uploading, setUploading] = useState({});
  const [newNotes, setNewNotes] = useState({});
  const [noteLoading, setNoteLoading] = useState({});
  const [noteAttachments, setNoteAttachments] = useState({});
  const [editingNote, setEditingNote] = useState(null);
  const [editNoteContent, setEditNoteContent] = useState('');
  const [submitAttempted, setSubmitAttempted] = useState({}); // Track submit attempts per step
  const [loiVerifications, setLoiVerifications] = useState({}); // LOI verification results per step
  const [analyzingLoi, setAnalyzingLoi] = useState({}); // LOI analysis in progress per step
  const fileInputRefs = useRef({});
  const noteFileRefs = useRef({});

  // Get theme colors with fallbacks
  const quoteTheme = userTheme?.quoteDetail || {};
  const PRIMARY_COLOR = quoteTheme.primaryColor || DEFAULT_PRIMARY;
  const SECONDARY_COLOR = quoteTheme.secondaryColor || DEFAULT_SECONDARY;
  const STEP_CARD_BG = quoteTheme.stepCardBg || '#FFFFFF';
  const STEP_CARD_BORDER = quoteTheme.stepCardBorder || '#E5E7EB';
  const ACTIVE_STEP_BG = quoteTheme.activeStepBg || '#F3F4F6';
  const LOCKED_STEP_TEXT = quoteTheme.lockedStepText || '#9CA3AF';
  const NOT_STARTED_HEADER_BG = quoteTheme.notStartedHeaderBg || '#F9FAFB';
  const PENDING_HEADER_BG = quoteTheme.pendingHeaderBg || '#FEF9C3';
  const APPROVED_HEADER_BG = quoteTheme.approvedHeaderBg || '#DCFCE7';
  const DENIED_HEADER_BG = quoteTheme.deniedHeaderBg || '#FEE2E2';

  useEffect(() => {
    fetchRequest();
  }, [id]);

  const fetchRequest = async (preserveLocalAnswers = false) => {
    try {
      const res = await api.get(`/quote-process/requests/${id}`);
      setRequest(res.data);
      
      // Initialize answers from existing data, but preserve local edits if specified
      if (!preserveLocalAnswers) {
        const answers = {};
        res.data.detailed_steps?.forEach(step => {
          answers[step.step_id] = step.answers || {};
        });
        setStepAnswers(answers);
      } else {
        // Merge server answers with local answers (local takes priority for unsaved edits)
        setStepAnswers(prev => {
          const merged = { ...prev };
          res.data.detailed_steps?.forEach(step => {
            // Only update if we don't have local edits for this step
            if (!merged[step.step_id] || Object.keys(merged[step.step_id]).length === 0) {
              merged[step.step_id] = step.answers || {};
            }
          });
          return merged;
        });
      }
      
      // Load existing LOI verifications for step 1 documents
      for (const step of res.data.detailed_steps || []) {
        if (step.order_number === 1 && step.documents?.length > 0) {
          for (const doc of step.documents) {
            if (doc.loi_verification_id && !loiVerifications[step.step_id]) {
              try {
                const verifRes = await api.get(`/loi-verification/verifications/${doc.loi_verification_id}`);
                if (verifRes.data?.result) {
                  setLoiVerifications(prev => ({
                    ...prev,
                    [step.step_id]: {
                      verification_id: doc.loi_verification_id,
                      summary: verifRes.data.result.summary,
                      grading: verifRes.data.result.grading,
                      checks: verifRes.data.result.checks,
                      contact_info: verifRes.data.result.contact_info,
                      metadata: verifRes.data.result.metadata,
                      intel_analysis: verifRes.data.result.intel_analysis,
                      is_paid_user: verifRes.data.result.is_paid_user,
                      partial: false,
                    }
                  }));
                }
              } catch (e) {
                // Verification data not accessible — skip
              }
            }
          }
        }
      }

      // Expand first unlocked step (only on initial load)
      if (!preserveLocalAnswers && res.data.detailed_steps?.length > 0) {
        const firstUnlockedIndex = res.data.unlocked_step - 1;
        const stepToExpand = res.data.detailed_steps[Math.max(0, Math.min(firstUnlockedIndex, res.data.detailed_steps.length - 1))];
        if (stepToExpand) {
          setExpandedSteps({ [stepToExpand.step_id]: true });
        }
      }
    } catch (error) {
      toast.error('Failed to load quote request');
      navigate('/admin/requests');
    } finally {
      setLoading(false);
    }
  };

  const toggleStep = (stepId) => {
    setExpandedSteps(prev => ({
      ...prev,
      [stepId]: !prev[stepId]
    }));
  };

  const handleAnswerChange = (stepId, questionId, value) => {
    setStepAnswers(prev => ({
      ...prev,
      [stepId]: {
        ...prev[stepId],
        [questionId]: value
      }
    }));
  };

  const handleSubmitStep = async (stepId) => {
    // Mark that submit was attempted for this step (to show validation errors)
    setSubmitAttempted(prev => ({ ...prev, [stepId]: true }));
    
    // Find the step to validate required questions
    const step = request?.detailed_steps?.find(s => s.step_id === stepId);
    if (step) {
      // Filter out "note" type as they are display-only and don't require answers
      const requiredQuestions = step.questions?.filter(q => q.is_required && q.field_type !== 'note') || [];
      const currentAnswers = stepAnswers[stepId] || {};
      
      // Check if all required questions have answers
      const missingRequired = requiredQuestions.filter(q => {
        const answer = currentAnswers[q.id];
        return !answer || answer.toString().trim() === '';
      });
      
      if (missingRequired.length > 0) {
        toast.error(`Please answer all required questions before submitting. Missing: ${missingRequired.length} required field(s)`);
        return;
      }
    }
    
    setSubmitting(prev => ({ ...prev, [stepId]: true }));
    try {
      await api.post(`/quote-process/requests/${id}/steps/${stepId}/submit`, {
        answers: stepAnswers[stepId] || {}
      });
      toast.success('Step submitted for review');
      setSubmitAttempted(prev => ({ ...prev, [stepId]: false })); // Reset on success
      fetchRequest(); // Full refresh after successful submit
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit step');
    } finally {
      setSubmitting(prev => ({ ...prev, [stepId]: false }));
    }
  };

  const handleFileUpload = async (stepId, event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploading(prev => ({ ...prev, [stepId]: true }));

    // Check if any file is a PDF on step 1 (LOI verification will run)
    const hasPdf = Array.from(files).some(f => f.name.toLowerCase().endsWith('.pdf'));
    const stepInfo = request?.detailed_steps?.find(s => s.step_id === stepId);
    const isStep1 = stepInfo?.order_number === 1;
    if (isStep1 && hasPdf) {
      setAnalyzingLoi(prev => ({ ...prev, [stepId]: true }));
    }

    let successCount = 0;
    let errorCount = 0;

    // Upload each file
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        const res = await api.post(`/quote-process/requests/${id}/steps/${stepId}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        successCount++;

        // Capture LOI verification result if present
        if (res.data?.loi_verification && !res.data.loi_verification.error) {
          setLoiVerifications(prev => ({ ...prev, [stepId]: res.data.loi_verification }));
        }
      } catch (error) {
        errorCount++;
        console.error(`Failed to upload ${file.name}:`, error);
      }
    }

    setAnalyzingLoi(prev => ({ ...prev, [stepId]: false }));

    // Show appropriate toast
    if (successCount > 0 && errorCount === 0) {
      const verif = loiVerifications[stepId];
      if (isStep1 && hasPdf) {
        toast.success('Document uploaded and LOI verification complete');
      } else {
        toast.success(`${successCount} document${successCount > 1 ? 's' : ''} uploaded`);
      }
    } else if (successCount > 0 && errorCount > 0) {
      toast.warning(`${successCount} uploaded, ${errorCount} failed`);
    } else {
      toast.error('Failed to upload documents');
    }

    fetchRequest(true); // Preserve local answers when refreshing after upload
    setUploading(prev => ({ ...prev, [stepId]: false }));
    
    // Reset file input
    if (fileInputRefs.current[stepId]) {
      fileInputRefs.current[stepId].value = '';
    }
  };

  const updateStepNotes = (stepId, updater) => {
    setRequest(prev => ({
      ...prev,
      detailed_steps: prev.detailed_steps.map(step =>
        step.step_id === stepId ? { ...step, notes: updater(step.notes || []) } : step
      )
    }));
  };

  const handleAddNote = async (stepId) => {
    const content = newNotes[stepId]?.trim();
    if (!content) return;

    const file = noteAttachments[stepId];
    setNoteLoading(prev => ({ ...prev, [stepId]: true }));
    try {
      let res;
      if (file) {
        const formData = new FormData();
        formData.append('content', content);
        formData.append('file', file);
        res = await api.post(`/quote-process/requests/${id}/steps/${stepId}/notes/with-attachment`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        res = await api.post(`/quote-process/requests/${id}/steps/${stepId}/notes`, { content });
      }
      const newNote = {
        id: res.data.id,
        content,
        author_id: user?.id,
        author_name: res.data.author_name,
        is_admin: res.data.is_admin,
        is_super_admin: res.data.is_super_admin || false,
        attachment: res.data.attachment || null,
        created_at: res.data.created_at,
      };
      updateStepNotes(stepId, notes => [...notes, newNote]);
      toast.success('Note added');
      setNewNotes(prev => ({ ...prev, [stepId]: '' }));
      setNoteAttachments(prev => ({ ...prev, [stepId]: null }));
      if (noteFileRefs.current[stepId]) noteFileRefs.current[stepId].value = '';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add note');
    } finally {
      setNoteLoading(prev => ({ ...prev, [stepId]: false }));
    }
  };

  const handleEditNote = async (stepId, noteId) => {
    if (!editNoteContent.trim()) return;
    try {
      await api.put(`/quote-process/requests/${id}/steps/${stepId}/notes/${noteId}`, { content: editNoteContent.trim() });
      updateStepNotes(stepId, notes =>
        notes.map(n => n.id === noteId ? { ...n, content: editNoteContent.trim(), edited: true } : n)
      );
      toast.success('Note updated');
      setEditingNote(null);
      setEditNoteContent('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update note');
    }
  };

  const canEditNote = (note) => {
    if (!user) return false;
    if (note.author_id !== user.id) return false;
    const created = new Date(note.created_at);
    const elapsed = (Date.now() - created.getTime()) / 1000;
    return elapsed <= 60;
  };

  const isStepUnlocked = (stepIndex) => {
    return stepIndex < request.unlocked_step;
  };

  const isStepEditable = (step, stepIndex) => {
    // Step is editable if it's unlocked and either not_started or denied
    return isStepUnlocked(stepIndex) && ['not_started', 'denied'].includes(step.status);
  };

  const getStatusStyles = (status) => {
    switch (status) {
      case 'approved':
        return { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', headerBgColor: APPROVED_HEADER_BG };
      case 'denied':
        return { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', headerBgColor: DENIED_HEADER_BG };
      case 'pending':
        return { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700', headerBgColor: PENDING_HEADER_BG };
      default:
        return { bg: 'bg-white', border: 'border-gray-200', text: 'text-gray-500', headerBgColor: NOT_STARTED_HEADER_BG };
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Format number as USD currency
  const formatPrice = (value) => {
    if (!value) return '';
    const numericValue = value.toString().replace(/[^0-9.]/g, '');
    const number = parseFloat(numericValue);
    if (isNaN(number)) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(number);
  };

  // Handle price input - store raw number, display formatted
  const handlePriceChange = (stepId, questionId, value) => {
    // Remove all non-numeric characters except decimal point
    const rawValue = value.replace(/[^0-9.]/g, '');
    // Ensure only one decimal point
    const parts = rawValue.split('.');
    const cleanValue = parts[0] + (parts.length > 1 ? '.' + parts.slice(1).join('') : '');
    handleAnswerChange(stepId, questionId, cleanValue);
  };

  // Validate email format
  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Handle checkbox changes (multiple selection)
  const handleCheckboxChange = (stepId, questionId, option, checked) => {
    const currentValue = stepAnswers[stepId]?.[questionId] || '';
    const currentSelections = currentValue ? currentValue.split('|||') : [];
    
    let newSelections;
    if (checked) {
      newSelections = [...currentSelections, option];
    } else {
      newSelections = currentSelections.filter(s => s !== option);
    }
    
    handleAnswerChange(stepId, questionId, newSelections.join('|||'));
  };

  // Handle multi-select dropdown
  const handleMultiSelectChange = (stepId, questionId, selectedOptions) => {
    const values = Array.from(selectedOptions).map(opt => opt.value);
    handleAnswerChange(stepId, questionId, values.join('|||'));
  };

  // Check if field should show validation error
  const shouldShowValidationError = (stepId, questionId, isRequired) => {
    if (!isRequired) return false;
    if (!submitAttempted[stepId]) return false;
    const value = stepAnswers[stepId]?.[questionId] || '';
    return !value || value.toString().trim() === '';
  };

  // Get input class with validation styling
  const getInputClass = (stepId, questionId, isRequired, baseClass) => {
    const showError = shouldShowValidationError(stepId, questionId, isRequired);
    if (showError) {
      return `${baseClass} border-red-400 bg-[#fbc6c6]`;
    }
    return baseClass;
  };

  // Render question field based on type
  const renderQuestionField = (q, stepId, editable) => {
    const value = stepAnswers[stepId]?.[q.id] || '';
    const baseInputClass = "w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-sm disabled:bg-gray-100 disabled:cursor-not-allowed";
    const showError = shouldShowValidationError(stepId, q.id, q.is_required);
    const inputClass = showError 
      ? `${baseInputClass} border-red-400 bg-[#fbc6c6]` 
      : baseInputClass;

    switch (q.field_type) {
      case 'note':
        // Notes are display-only - render HTML content (supports links, formatting)
        return (
          <div 
            className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800 prose prose-sm prose-blue max-w-none [&_a]:text-blue-600 [&_a]:underline [&_a:hover]:text-blue-800"
            dangerouslySetInnerHTML={{ __html: q.question_text }}
          />
        );

      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleAnswerChange(stepId, q.id, e.target.value)}
            disabled={!editable}
            rows={3}
            placeholder="Enter your response..."
            className={inputClass}
            data-testid={`question-${q.id}`}
          />
        );

      case 'number':
        return (
          <input
            type="text"
            inputMode="numeric"
            value={value}
            onChange={(e) => {
              const numValue = e.target.value.replace(/[^0-9.-]/g, '');
              handleAnswerChange(stepId, q.id, numValue);
            }}
            disabled={!editable}
            placeholder="Enter a number"
            className={inputClass}
            data-testid={`question-${q.id}`}
          />
        );

      case 'email':
        return (
          <div>
            <input
              type="email"
              value={value}
              onChange={(e) => handleAnswerChange(stepId, q.id, e.target.value)}
              disabled={!editable}
              placeholder="email@example.com"
              className={`${inputClass} ${value && !isValidEmail(value) ? 'border-red-300 focus:ring-red-200' : ''}`}
              data-testid={`question-${q.id}`}
            />
            {value && !isValidEmail(value) && (
              <p className="text-xs text-red-500 mt-1">Please enter a valid email address</p>
            )}
          </div>
        );

      case 'phone':
        return (
          <input
            type="tel"
            value={value}
            onChange={(e) => handleAnswerChange(stepId, q.id, e.target.value)}
            disabled={!editable}
            placeholder="+1 (555) 123-4567"
            className={inputClass}
            data-testid={`question-${q.id}`}
          />
        );

      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => handleAnswerChange(stepId, q.id, e.target.value)}
            disabled={!editable}
            className={inputClass}
            data-testid={`question-${q.id}`}
          />
        );

      case 'price':
        return (
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">$</span>
            <input
              type="text"
              inputMode="decimal"
              value={value ? parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}
              onChange={(e) => handlePriceChange(stepId, q.id, e.target.value)}
              disabled={!editable}
              placeholder="0.00"
              className={`${inputClass} pl-8`}
              data-testid={`question-${q.id}`}
            />
          </div>
        );

      case 'select':
        if (q.allow_multiple) {
          // Multi-select dropdown
          const selectedValues = value ? value.split('|||') : [];
          return (
            <select
              multiple
              value={selectedValues}
              onChange={(e) => handleMultiSelectChange(stepId, q.id, e.target.selectedOptions)}
              disabled={!editable}
              className={`${inputClass} min-h-[100px]`}
              data-testid={`question-${q.id}`}
            >
              {q.options?.map((opt, i) => (
                <option key={i} value={opt}>{opt}</option>
              ))}
            </select>
          );
        }
        // Single select dropdown
        return (
          <select
            value={value}
            onChange={(e) => handleAnswerChange(stepId, q.id, e.target.value)}
            disabled={!editable}
            className={inputClass}
            data-testid={`question-${q.id}`}
          >
            <option value="">Select an option</option>
            {q.options?.map((opt, i) => (
              <option key={i} value={opt}>{opt}</option>
            ))}
          </select>
        );

      case 'checkbox':
        const selectedCheckboxes = value ? value.split('|||') : [];
        return (
          <div className={`space-y-2 p-2 rounded-lg ${showError ? 'bg-[#fbc6c6]' : ''}`}>
            {q.options?.map((opt, i) => (
              <label key={i} className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedCheckboxes.includes(opt)}
                  onChange={(e) => handleCheckboxChange(stepId, q.id, opt, e.target.checked)}
                  disabled={!editable}
                  className="w-4 h-4 rounded border-gray-300 disabled:opacity-50"
                  style={{ accentColor: PRIMARY_COLOR }}
                />
                <span className={`text-sm ${!editable ? 'text-gray-400' : 'text-gray-700'}`}>{opt}</span>
              </label>
            ))}
          </div>
        );

      case 'radio':
        return (
          <div className={`space-y-2 p-2 rounded-lg ${showError ? 'bg-[#fbc6c6]' : ''}`}>
            {q.options?.map((opt, i) => (
              <label key={i} className="flex items-center gap-3 cursor-pointer">
                <input
                  type="radio"
                  name={`radio-${q.id}`}
                  checked={value === opt}
                  onChange={() => handleAnswerChange(stepId, q.id, opt)}
                  disabled={!editable}
                  className="w-4 h-4 border-gray-300 disabled:opacity-50"
                  style={{ accentColor: PRIMARY_COLOR }}
                />
                <span className={`text-sm ${!editable ? 'text-gray-400' : 'text-gray-700'}`}>{opt}</span>
              </label>
            ))}
          </div>
        );

      case 'file':
        return (
          <div>
            {value && (
              <div className="flex items-center gap-2 mb-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
                <FileText className="w-4 h-4 text-gray-400" />
                <a
                  href={resolveFileUrl(value, process.env.REACT_APP_BACKEND_URL)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium hover:underline"
                  style={{ color: PRIMARY_COLOR }}
                >
                  View uploaded file
                </a>
              </div>
            )}
            {editable && (
              <div className="relative border-2 border-dashed border-gray-300 rounded-xl p-4 text-center hover:border-gray-400 hover:bg-gray-50 transition-all">
                <input
                  type="file"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const formData = new FormData();
                    formData.append('file', file);
                    try {
                      const res = await api.post(`/quote-process/requests/${id}/steps/${stepId}/upload`, formData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                      });
                      handleAnswerChange(stepId, q.id, res.data.file_url);
                      toast.success(`${file.name} uploaded`);
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Failed to upload file');
                    }
                    e.target.value = '';
                  }}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  data-testid={`question-file-${q.id}`}
                />
                <div className="flex flex-col items-center gap-1 text-gray-500 pointer-events-none">
                  <Upload className="w-5 h-5" />
                  <span className="text-sm">Click to upload a file</span>
                  <span className="text-xs text-gray-400">PDF, DOC, XLS, Images, etc.</span>
                </div>
              </div>
            )}
          </div>
        );

      case 'text':
      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleAnswerChange(stepId, q.id, e.target.value)}
            disabled={!editable}
            placeholder="Enter your response..."
            className={inputClass}
            data-testid={`question-${q.id}`}
          />
        );
    }
  };

  if (loading) {
    return (
      <Sidebar>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-300" style={{ borderTopColor: PRIMARY_COLOR }}></div>
        </div>
      </Sidebar>
    );
  }

  if (!request) return null;

  return (
    <Sidebar>
      <div className="p-8 bg-gray-50/50 min-h-screen">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin/requests')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 text-sm"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to My Requests
          </button>
          
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Request a Quote</h1>
              <p className="text-gray-500 text-sm mt-1">Complete each step to finalize your quote request</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500">Request ID</p>
              <p className="font-mono text-sm text-gray-700">#{id?.slice(-8)}</p>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="bg-white rounded-xl border border-gray-100 p-5 mb-6">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-700">Progress</span>
            <span className="text-sm text-gray-500">
              {request.detailed_steps?.filter(s => s.status === 'approved').length} of {request.detailed_steps?.length} steps completed
            </span>
          </div>
          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full rounded-full transition-all duration-500"
              style={{ 
                width: `${(request.detailed_steps?.filter(s => s.status === 'approved').length / request.detailed_steps?.length * 100) || 0}%`,
                backgroundColor: PRIMARY_COLOR
              }}
            />
          </div>
        </div>

        {/* Steps */}
        <div className="space-y-4">
          {request.detailed_steps?.map((step, index) => {
            const statusStyles = getStatusStyles(step.status);
            const isExpanded = expandedSteps[step.step_id];
            const unlocked = isStepUnlocked(index);
            const editable = isStepEditable(step, index);

            return (
              <div 
                key={step.step_id}
                className={`rounded-xl border overflow-hidden transition-all ${statusStyles.border}`}
                data-testid={`step-${step.step_id}`}
              >
                {/* Step Header */}
                <div 
                  className="px-6 py-4 flex items-center justify-between cursor-pointer transition hover:brightness-95"
                  style={{ backgroundColor: statusStyles.headerBgColor }}
                  onClick={() => toggleStep(step.step_id)}
                >
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      {isExpanded ? (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      )}
                      <span className="text-sm font-semibold text-gray-500">Step {step.order_number}</span>
                    </div>
                    <h3 className="font-medium text-gray-900">{step.name}</h3>
                    {!step.is_required && (
                      <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-600 rounded">Optional</span>
                    )}
                    {!unlocked && (
                      <Lock className="w-4 h-4 text-gray-400" />
                    )}
                  </div>
                  <span className={`font-bold text-sm uppercase tracking-wide ${statusStyles.text}`}>
                    {step.status === 'not_started' ? 'Not Started' : step.status}
                  </span>
                </div>

                {/* Step Content */}
                {isExpanded && (
                  <div className={`px-6 pb-6 pt-4 ${statusStyles.bg}`}>
                    {step.description && (
                      <p className="text-sm text-gray-600 mb-4 pb-4 border-b border-gray-100">{step.description}</p>
                    )}

                    {/* Questions */}
                    {step.questions?.length > 0 && (
                      <div className="mb-6">
                        <h4 className="text-sm font-semibold text-gray-700 mb-4">Questions</h4>
                        <div className="space-y-4">
                          {step.questions.map((q) => (
                            <div key={q.id}>
                              {/* Note field type renders its own content - no label needed */}
                              {q.field_type !== 'note' && (
                                <label className="block text-sm font-medium text-gray-700 mb-2 [&_a]:text-blue-600 [&_a]:underline [&_a:hover]:text-blue-800">
                                  <span dangerouslySetInnerHTML={{ __html: q.question_text }} />
                                  {q.is_required && <span className="text-red-500 ml-1">*</span>}
                                </label>
                              )}
                              {renderQuestionField(q, step.step_id, editable)}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Document Upload */}
                    {step.allow_upload_documents && (
                      <div className="mb-6">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          Documents
                        </h4>
                        
                        {/* Existing Documents */}
                        {step.documents?.length > 0 && (
                          <div className="space-y-2 mb-3">
                            {step.documents.map((doc) => (
                              <div key={doc.id} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-3">
                                <div className="flex items-center gap-3">
                                  <FileText className="w-5 h-5 text-gray-400" />
                                  <div>
                                    <p className="text-sm font-medium text-gray-900">{doc.file_name}</p>
                                    <p className="text-xs text-gray-500">{formatFileSize(doc.file_size)}</p>
                                  </div>
                                </div>
                                <a
                                  href={resolveFileUrl(doc.file_url, process.env.REACT_APP_BACKEND_URL)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm font-medium hover:underline"
                                  style={{ color: PRIMARY_COLOR }}
                                >
                                  View
                                </a>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* LOI Verification Analysis (Step 1 only) */}
                        {analyzingLoi[step.step_id] && (
                          <div className="mb-4 p-6 bg-blue-50 border border-blue-200 rounded-xl text-center">
                            <RefreshCw className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-3" />
                            <p className="text-sm font-semibold text-blue-700">Analyzing your LOI...</p>
                            <p className="text-xs text-blue-500 mt-1">Running 40+ verification checks across 8 forensic categories</p>
                          </div>
                        )}

                        {loiVerifications[step.step_id] && !loiVerifications[step.step_id].error && (
                          <div className="mb-4 space-y-4">
                            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-green-500" />
                              LOI Verification Report
                            </h4>

                            {/* Grade Card */}
                            <GradeHeroCard result={loiVerifications[step.step_id]} />

                            {/* Score Cards */}
                            {loiVerifications[step.step_id].grading && (
                              <div className="grid grid-cols-3 gap-3">
                                <ScoreCard label="Must Have" value={loiVerifications[step.step_id].grading.level_1} color="red" />
                                <ScoreCard label="Important" value={loiVerifications[step.step_id].grading.level_2} color="orange" />
                                <ScoreCard label="Nice to Have" value={loiVerifications[step.step_id].grading.level_3} color="blue" />
                              </div>
                            )}

                            {/* Download Verification Report */}
                            {loiVerifications[step.step_id].verification_id && (
                              <button
                                onClick={async () => {
                                  try {
                                    toast.info('Generating report...');
                                    const res = await api.get(`/loi-verification/report/${loiVerifications[step.step_id].verification_id}`, {
                                      responseType: 'blob',
                                      timeout: 120000,
                                    });
                                    const blob = new Blob([res.data], { type: 'application/pdf' });
                                    const url = window.URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = 'LOI_Verified_Report.pdf';
                                    document.body.appendChild(a);
                                    a.click();
                                    document.body.removeChild(a);
                                    window.URL.revokeObjectURL(url);
                                    toast.success('Report downloaded');
                                  } catch (err) {
                                    console.error('Report download error:', err);
                                    toast.error('Failed to download report');
                                  }
                                }}
                                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition"
                                style={{ backgroundColor: PRIMARY_COLOR }}
                              >
                                <Download className="w-4 h-4" />
                                Download Verified LOI Report (PDF)
                              </button>
                            )}

                            {/* Intel Analysis */}
                            {loiVerifications[step.step_id].intel_analysis && (
                              <IntelAnalysisBox
                                intel={loiVerifications[step.step_id].intel_analysis}
                                isPaidUser={loiVerifications[step.step_id].is_paid_user}
                              />
                            )}

                            {/* Full Report */}
                            <FullReport
                              result={loiVerifications[step.step_id]}
                              isPaidUser={loiVerifications[step.step_id].is_paid_user}
                            />
                          </div>
                        )}

                        {/* Upload Area with Drag & Drop - only if editable */}
                        {editable && (
                          <div
                            className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-all ${
                              uploading[step.step_id]
                                ? 'border-blue-400 bg-blue-50'
                                : step.documents?.length === 0
                                  ? 'border-yellow-400 bg-yellow-50/30 animate-pulse-border'
                                  : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                            }`}
                            style={step.documents?.length === 0 && !uploading[step.step_id] ? {
                              animation: 'loi-upload-glow 2s ease-in-out infinite',
                            } : {}}
                            onDragOver={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              e.currentTarget.classList.add('border-blue-500', 'bg-blue-50');
                            }}
                            onDragLeave={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
                            }}
                            onDrop={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
                              if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                                // Create a synthetic event object for handleFileUpload
                                const syntheticEvent = { target: { files: e.dataTransfer.files } };
                                handleFileUpload(step.step_id, syntheticEvent);
                              }
                            }}
                            data-testid={`dropzone-${step.step_id}`}
                          >
                            <input
                              type="file"
                              ref={el => fileInputRefs.current[step.step_id] = el}
                              onChange={(e) => handleFileUpload(step.step_id, e)}
                              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                              id={`file-upload-${step.step_id}`}
                              multiple
                            />
                            {uploading[step.step_id] ? (
                              <div className="flex items-center justify-center gap-2 text-blue-600">
                                <RefreshCw className="w-5 h-5 animate-spin" />
                                <span>Uploading...</span>
                              </div>
                            ) : (
                              <div className="flex flex-col items-center gap-2 text-gray-500 pointer-events-none">
                                <div className="p-3 bg-gray-100 rounded-full">
                                  <Upload className="w-6 h-6" />
                                </div>
                                <span className="text-sm font-medium">Drop files here or click to upload</span>
                                <span className="text-xs text-gray-400">PDF, DOC, XLS, Images, ZIP, etc.</span>
                              </div>
                            )}
                          </div>
                        )}

                        {!editable && step.documents?.length === 0 && (
                          <p className="text-sm text-gray-400 italic">No documents uploaded</p>
                        )}
                      </div>
                    )}

                    {/* Notes Section */}
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        <MessageSquare className="w-4 h-4" />
                        Discussion / Questions and Document Revisions ({step.notes?.length || 0})
                      </h4>

                      {/* Notes List */}
                      <div className="space-y-3 mb-4 max-h-80 overflow-y-auto">
                        {step.notes?.length > 0 ? (
                          step.notes.map((note) => (
                            <div
                              key={note.id}
                              className={`rounded-lg p-3 ${note.is_super_admin ? 'bg-orange-50' : note.is_admin ? 'bg-green-50' : 'bg-yellow-50'}`}
                            >
                              {editingNote === note.id ? (
                                <div className="space-y-2">
                                  <textarea
                                    value={editNoteContent}
                                    onChange={(e) => setEditNoteContent(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none"
                                    rows={3}
                                  />
                                  <div className="flex justify-end gap-2">
                                    <button
                                      onClick={() => { setEditingNote(null); setEditNoteContent(''); }}
                                      className="px-3 py-1.5 text-gray-600 text-sm"
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      onClick={() => handleEditNote(step.step_id, note.id)}
                                      className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm"
                                    >
                                      Save
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-xs font-medium text-gray-700">{note.author_name}</span>
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs text-gray-500">
                                        {new Date(note.created_at).toLocaleString()}
                                      </span>
                                      {canEditNote(note) && (
                                        <button
                                          onClick={() => { setEditingNote(note.id); setEditNoteContent(note.content); }}
                                          className="p-1 text-gray-400 hover:text-gray-600"
                                          title="Edit (within 60s)"
                                        >
                                          <Edit2 className="w-3 h-3" />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                  <p className="text-sm text-gray-700">{note.content}</p>
                                  {note.edited && <p className="text-xs text-gray-400 mt-1">(edited)</p>}
                                  {note.attachment && (
                                    <div className="mt-2">
                                      {isImageFile(note.attachment.file_name) ? (
                                        <a href={note.attachment.file_url} target="_blank" rel="noopener noreferrer">
                                          <img
                                            src={note.attachment.file_url}
                                            alt={note.attachment.file_name}
                                            className="max-w-xs max-h-48 rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition"
                                          />
                                          <span className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                                            <Image className="w-3 h-3" />
                                            {note.attachment.file_name}
                                          </span>
                                        </a>
                                      ) : (
                                        <a
                                          href={note.attachment.file_url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="flex items-center gap-2 text-xs text-blue-600 hover:text-blue-800 bg-white/60 rounded px-2 py-1 w-fit"
                                        >
                                          <Paperclip className="w-3 h-3" />
                                          {note.attachment.file_name}
                                          <Download className="w-3 h-3" />
                                        </a>
                                      )}
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-gray-400 italic">No notes yet. Start a conversation!</p>
                        )}
                      </div>

                      {/* Add Note - Always available for unlocked steps */}
                      {unlocked && (
                        <div className="space-y-2">
                          {noteAttachments[step.step_id] && (
                            <div className="flex items-center gap-2 text-xs text-gray-600 bg-gray-50 rounded px-2 py-1">
                              <Paperclip className="w-3 h-3" />
                              <span className="truncate">{noteAttachments[step.step_id].name}</span>
                              <button
                                onClick={() => {
                                  setNoteAttachments(prev => ({ ...prev, [step.step_id]: null }));
                                  if (noteFileRefs.current[step.step_id]) noteFileRefs.current[step.step_id].value = '';
                                }}
                                className="text-gray-400 hover:text-red-500"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          )}
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={newNotes[step.step_id] || ''}
                              onChange={(e) => setNewNotes(prev => ({ ...prev, [step.step_id]: e.target.value }))}
                              placeholder="Add a note or question..."
                              className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2"
                              onKeyPress={(e) => e.key === 'Enter' && !noteAttachments[step.step_id] && handleAddNote(step.step_id)}
                            />
                            <input
                              type="file"
                              ref={el => noteFileRefs.current[step.step_id] = el}
                              className="hidden"
                              onChange={(e) => {
                                if (e.target.files[0]) {
                                  setNoteAttachments(prev => ({ ...prev, [step.step_id]: e.target.files[0] }));
                                }
                              }}
                            />
                            <button
                              onClick={() => noteFileRefs.current[step.step_id]?.click()}
                              className="px-3 py-2 border border-gray-200 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                              title="Attach file or photo"
                            >
                              <Paperclip className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleAddNote(step.step_id)}
                              disabled={noteLoading[step.step_id] || !newNotes[step.step_id]?.trim()}
                              className="px-4 py-2 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                              style={{ backgroundColor: PRIMARY_COLOR }}
                            >
                              {noteLoading[step.step_id] ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                              ) : (
                                <Send className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Admin Feedback for Denied */}
                    {step.status === 'denied' && step.admin_notes && (
                      <div className="mb-6 p-4 bg-red-100 border border-red-200 rounded-xl">
                        <div className="flex items-start gap-2">
                          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-red-700">Revision Required</p>
                            <p className="text-sm text-red-600 mt-1">{step.admin_notes}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Submit Button */}
                    {editable && (
                      <div className="pt-4 border-t border-gray-200">
                        <button
                          onClick={() => handleSubmitStep(step.step_id)}
                          disabled={submitting[step.step_id]}
                          className="w-full flex items-center justify-center gap-2 px-6 py-3 text-white rounded-xl font-medium transition disabled:opacity-50"
                          style={{ backgroundColor: PRIMARY_COLOR }}
                          data-testid={`submit-step-${step.step_id}`}
                        >
                          {submitting[step.step_id] ? (
                            <>
                              <RefreshCw className="w-5 h-5 animate-spin" />
                              Submitting...
                            </>
                          ) : (
                            <>
                              <CheckCircle className="w-5 h-5" />
                              {step.status === 'denied' ? 'Resubmit for Review' : 'Submit for Review'}
                            </>
                          )}
                        </button>
                      </div>
                    )}

                    {/* Locked Message */}
                    {!unlocked && (
                      <div className="pt-4 border-t border-gray-200">
                        <div className="flex items-center justify-center gap-2 text-gray-400 py-3">
                          <Lock className="w-5 h-5" />
                          <span className="text-sm">Complete previous required steps to unlock</span>
                        </div>
                      </div>
                    )}

                    {/* Pending Message */}
                    {step.status === 'pending' && (
                      <div className="pt-4 border-t border-gray-200">
                        <div className="flex items-center justify-center gap-2 text-yellow-600 py-3">
                          <Clock className="w-5 h-5" />
                          <span className="text-sm">Awaiting admin review</span>
                        </div>
                      </div>
                    )}

                    {/* Approved Message */}
                    {step.status === 'approved' && (
                      <div className="pt-4 border-t border-gray-200">
                        <div className="flex items-center justify-center gap-2 text-green-600 py-3">
                          <CheckCircle className="w-5 h-5" />
                          <span className="text-sm">This step has been approved</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </Sidebar>
  );
};

export default QuoteRequestDetail;
